import { useEffect, useMemo, useRef, useState } from 'react';
import { captureSummaryV3, facilitiesLeaderboard, myFacilityBenchmark, type LeaderboardEntry, type PeerTier } from '@aithora/shared';
import { useConsoleRole } from '../../session/SessionContext';
import { facilitiesParam, usePickedIds } from '../../session/FacilityScope';
import { ScopeEmpty, noFacilitiesIn } from '../../ui/ScopeEmpty';
import { usePageTab } from '../../shell/Shell';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { sx } from '../../ui/designStyles';
import { exportCsv } from '../../ui/exportCsv';
import { PerfContext, type Bench, type PerfCtx, type TabId } from './ctx';
import { downloadQbrPdf } from './qbr';
import { COLS, TIERS, gradeOf, pct100, pre, tabStyle, usePerfApi } from './lib';
import { SummaryTab } from './SummaryTab';
import { EfficiencyTab } from './EfficiencyTab';
import { RebatesTab } from './RebatesTab';
import { PortfolioTab } from './PortfolioTab';
import { DistributionTab } from './DistributionTab';
import { TrajectoryTab } from './TrajectoryTab';
import { SurveyReadyTab } from './SurveyReadyTab';
import { AdmissionsTab } from './AdmissionsTab';
import { CredentialsTab } from './CredentialsTab';
import { MoreMenu } from '../../ui/MoreMenu';

const TAB_LABEL: Record<TabId, string> = {
  overview: 'Summary',
  survey: 'Survey ready',
  creds: 'Credentials',
  admit: 'Admissions',
  careops: 'Efficiency',
  rebates: 'Rebates / Reimbursement',
  portfolio: 'Portfolio',
  distribution: 'Distribution',
  trajectory: 'Trajectory',
};

const fromEntry = (e: LeaderboardEntry): Bench => ({
  orgId: e.id,
  name: e.name,
  ccn: e.ccn,
  beds: e.beds,
  composite: e.composite,
  grade: e.grade,
  peerN: e.peerN,
  insufficient: !!e.insufficient,
  metrics: e.metrics ?? {},
  residents: e.residents ?? null,
});

/** "May 20 → Aug 18" for the trailing 90 days (design facWindow title). */
function windowTitle(now: number): string {
  const f = (t: number) => new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return 'Facility performance · ' + f(now - 90 * 86_400_000) + ' → ' + f(now) + ', trailing 90 days';
}

/** The tabs that stay in the row (V9: the rest sit under More). Trajectory stays in the row too (Andrew, 2026-10-29). */
export const PERF_KEEP: TabId[] = ['overview', 'survey', 'admit', 'careops', 'trajectory'];

/**
 * The tabs a role sees, in the design's order (ConsoleV9 scTabs): Summary, Survey ready,
 * Credentials and Admissions (the Facility Admin and the Aithora team), Efficiency, Rebates /
 * Reimbursement, Portfolio (everyone but the Facility Admin), Distribution and Trajectory.
 */
export function perfTabs(role: string | null): TabId[] {
  const fa = role === 'ADMIN';
  const team = role === 'SUPER_ADMIN' || role === 'OPERATOR';
  return (['overview', 'survey'] as TabId[])
    .concat(fa || team ? ['creds', 'admit'] : [])
    .concat(['careops', 'rebates'])
    .concat(fa ? [] : ['portfolio'])
    .concat(['distribution', 'trajectory']);
}

/**
 * Performance (design isScorecard; V9 ConsoleV9 L18413): the tabs above, in one row with a
 * More menu at the end, with the peer tier, the case-mix switch and the facility picker. It
 * always opens on Summary. A Facility Admin sees their own building only; the server pins
 * it, and peers are percentiles, never names.
 */
export function PerformancePage() {
  const { role, realRole } = useConsoleRole();
  const roReason = useReadOnlyReason();
  const fa = role === 'ADMIN';
  const op = !fa;
  const ro = role === 'PARTNER' || realRole === 'PARTNER' || roReason !== null;
  const canAct = (role === 'SUPER_ADMIN' || role === 'OPERATOR' || fa) && roReason === null;
  const [tab, setTab] = useState<TabId>('overview');
  // Performance always opens on Summary, and a role switch lands there too (V9 review).
  useEffect(() => setTab('overview'), [role]);
  const [tier, setTier] = useState<PeerTier>('national');
  const [printBlocked, setPrintBlocked] = useState(false);
  const [caseMix, setCaseMix] = useState(false);
  const [beds, setBeds] = useState<'All' | '<100' | '100+'>('All');
  const [picked, setPicked] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const tabs = perfTabs(role);
  const shownTab: TabId = tabs.includes(tab) ? tab : 'overview';
  usePageTab(TAB_LABEL[shownTab]);

  // The facility switcher (V6): the operator's buildings are the picked ones.
  const pickIds = usePickedIds();
  const facilities = op ? facilitiesParam(pickIds) : undefined;
  const board = usePerfApi((c, signal) => facilitiesLeaderboard(c, { query: { tier, caseMix: caseMix || undefined, facilities }, signal }), [tier, caseMix, facilities], op);
  const own = usePerfApi((c, signal) => myFacilityBenchmark(c, { query: { tier, caseMix: caseMix || undefined }, signal }), [tier, caseMix], fa);

  const fac: Bench | null = useMemo(() => {
    if (op) {
      const list = board.data?.facilities ?? [];
      const hit = (picked && list.find((f) => f.id === picked)) || list.find((f) => f.composite != null) || list[0];
      return hit ? fromEntry(hit) : null;
    }
    const b = own.data;
    if (!b || !b.linked) return null;
    return {
      orgId: null,
      name: b.name ?? b.orgName ?? '–',
      ccn: b.ccn ?? null,
      beds: b.beds ?? null,
      composite: b.composite ?? null,
      grade: b.grade ?? null,
      peerN: b.peerN ?? null,
      insufficient: !!b.insufficient,
      metrics: b.metrics ?? {},
      residents: b.residents ?? null,
    };
  }, [op, board.data, own.data, picked]);

  // Residents in the building: the capture summary counts them (the server pins a Facility Admin).
  const cap = usePerfApi((c, signal) => captureSummaryV3(c, { query: { facility: op ? fac?.orgId ?? undefined : undefined }, signal }), [op ? fac?.orgId : 'own'], op ? !!fac?.orgId : true);
  // In the building now (in-house.ts), the Overview's count; the sheet's own count of every
  // resident account is the fallback for an older server.
  const residents = fac?.residents ?? cap.data?.residentsInHouse ?? cap.data?.residents ?? null;

  const peer = (op ? board.data?.peer : own.data?.peer) ?? null;
  const peerN = peer?.n ?? fac?.peerN ?? null;
  const peerName = peer?.name ?? 'national';
  const peerLabel = peerN != null && peerN < 11 ? peerName + ' peers: fewer than 11, comparison hidden' : peerName + ' peers (n=' + (peerN ?? 0).toLocaleString('en-US') + ')';

  useEffect(() => {
    if (!pickerOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPickerOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [pickerOpen]);

  const ctx: PerfCtx = {
    op,
    fa,
    ro,
    canAct,
    roReason,
    tier,
    caseMix,
    setCaseMix,
    fac: fac ? { ...fac, residents } : null,
    facState: { loading: (op ? board.loading : own.loading) && !fac, error: (op ? board.error : own.error)?.message ?? (own.data && !own.data.linked ? own.data.reason ?? 'No CMS facility is linked to this building yet.' : null) },
    board: op ? board : null,
    peer,
    peerLabel,
    beds,
    setBeds,
    goTab: setTab,
    pickFacility: (id) => {
      setPicked(id);
      setTab('overview');
    },
    facilities,
  };

  const flexPane = shownTab !== 'rebates';
  const paneStyle = flexPane
    ? 'flex:1 1 auto;min-height:0;overflow:hidden;min-width:0;display:flex;flex-direction:column;'
    : 'flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;min-width:0;display:grid;grid-template-columns:repeat(auto-fit,minmax(430px,1fr));gap:12px 16px;align-content:start;align-items:start;padding-right:3px;';

  const exportQbr = () => {
    if (!fac) return;
    exportCsv(
      ['Metric', 'Value', 'Percentile', 'Peers'],
      COLS.map((c) => {
        const m = fac.metrics[c.key];
        return [c.label, m?.value ?? '', pct100(m?.percentile) ?? '', peerLabel];
      }),
      'aithora-qbr-' + (fac.ccn ?? 'facility') + '.csv',
    );
  };

  const list = board.data?.facilities ?? [];
  // A pick with none of the operator's buildings in it (design geoLayer, scorecard).
  if (op && facilities && board.data && !list.length) {
    return <ScopeEmpty title={noFacilitiesIn} body="Facility performance comes from the buildings connected to Aithora. None of them is in this view." />;
  }
  return (
    <PerfContext.Provider value={ctx}>
      <div data-screen-label="Performance" data-testid="performance" style={sx('display:flex;flex-direction:column;gap:7px;min-width:0;flex:1 1 auto;min-height:0;height:100%;overflow:hidden;')}>
        <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:14px;flex-wrap:wrap;row-gap:8px;min-width:0;')}>
          <div style={sx('min-width:0;flex:1 1 260px;display:flex;align-items:baseline;gap:10px;flex-wrap:nowrap;overflow:hidden;')}>
            <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:4px;min-width:0;')}>
              <h1 style={sx('margin:0;font-size:17px;font-weight:800;letter-spacing:-.02em;white-space:nowrap;')}>Performance</h1>
              <span style={sx('display:inline-flex;align-items:center;gap:6px;font-size:14px;font-weight:700;letter-spacing:.06em;color:#1F6B52;background:rgba(47,138,111,.13);border-radius:20px;padding:3px 10px;')}>
                <span style={sx('width:6px;height:6px;border-radius:50%;background:#1F6B52;animation:bl 1.8s infinite;')} />
                LIVE
              </span>
            </div>
            <div ref={pickerRef} style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;font-size:16px;color:#54606C;position:relative;row-gap:3px;min-width:0;')}>
              {op && (
                <a
                  href="#"
                  aria-haspopup="listbox"
                  aria-expanded={pickerOpen}
                  onClick={(e) => {
                    pre(e);
                    setPickerOpen((o) => !o);
                  }}
                  style={sx('display:inline-flex;align-items:center;gap:7px;font-weight:600;color:#22272E;text-decoration:none;border-bottom:1px dashed rgba(34,39,46,.28);')}
                >
                  <span>{fac?.name ?? '–'}</span>
                  <span style={sx('color:#54606C;font-weight:400;')}>{'▾'}</span>
                </a>
              )}
              {fa && (
                <span style={sx('font-weight:600;color:#22272E;')}>
                  <span>{fac?.name ?? '–'}</span>
                </span>
              )}
              <span style={sx('white-space:nowrap;')}>
                <span style={sx('color:#54606C;')}>·</span> <span>{(fac?.beds ?? '–') + ' beds · ' + (residents ?? '–') + ' residents'}</span>
              </span>
              <span title={windowTitle(Date.now())} style={sx('color:#54606C;cursor:default;')}>
                · 90d
              </span>
              {pickerOpen && op && (
                <div role="listbox" aria-label="Facilities" style={sx('position:absolute;top:28px;left:0;z-index:20;min-width:330px;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);border-radius:10px;box-shadow:0 12px 30px rgba(34,39,46,.14);padding:5px;display:flex;flex-direction:column;')}>
                  {list.map((r) => {
                    const on = r.id === fac?.orgId;
                    return (
                      <a
                        key={r.id}
                        href="#"
                        role="option"
                        aria-selected={on}
                        onClick={(e) => {
                          pre(e);
                          setPicked(r.id);
                          setPickerOpen(false);
                        }}
                        style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;padding:9px 13px;font-size:16px;text-decoration:none;color:#22272E;' + (on ? 'background:rgba(79,138,214,.1);font-weight:700;' : ''))}
                      >
                        <span style={sx('min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}>{r.name}</span>
                        <span style={sx('flex:1;')} />
                        <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;flex:0 1 auto;min-width:0;')}>
                          {'CCN ' + (r.ccn ?? '–') + ' · ' + (r.beds ?? '–') + ' beds · ' + (r.composite != null ? (r.grade ?? gradeOf(r.composite)) + ' ' + r.composite : 'not scored')}
                        </span>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <span style={sx('flex:1;')} />
          <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;flex:0 1 auto;min-width:0;')}>
            <span style={sx('font-size:14px;letter-spacing:.13em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Peers</span>
            <div role="group" aria-label="Peer tier" style={sx('display:inline-flex;flex:0 1 auto;min-width:0;border:1px solid rgba(34,39,46,.16);border-radius:8px;overflow:hidden;background:#FCFAF4;')}>
              {TIERS.map((t) => {
                const on = tier === t.tier;
                return (
                  <a
                    key={t.tier}
                    href="#"
                    aria-pressed={on}
                    onClick={(e) => {
                      pre(e);
                      setTier(t.tier);
                    }}
                    style={sx('display:inline-block;padding:6px 13px;font-size:16px;font-weight:' + (on ? '700' : '500') + ';text-decoration:none;color:' + (on ? '#fff' : '#54606C') + ';background:' + (on ? '#2F5C97' : 'transparent') + ';')}
                  >
                    {t.label}
                  </a>
                );
              })}
            </div>
            <a
              href="#"
              aria-pressed={caseMix}
              onClick={(e) => {
                pre(e);
                setCaseMix((c) => !c);
              }}
              style={sx(
                'display:inline-flex;align-items:center;gap:8px;height:30px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;box-sizing:border-box;white-space:nowrap;' +
                  (caseMix ? 'background:#2F5C97;color:#fff;border:1px solid #2F5C97;' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);'),
              )}
            >
              Case-mix adjusted
            </a>
          </div>
          <a
            href="#"
            onClick={(e) => {
              pre(e);
              exportQbr();
            }}
            style={sx('display:inline-flex;align-items:center;gap:7px;height:30px;padding:0 12px;border:1px solid rgba(34,39,46,.14);border-radius:8px;color:#22272E;font-size:14px;font-weight:600;text-decoration:none;flex:0 1 auto;min-width:0;white-space:nowrap;')}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="3" width="13" height="16" rx="1.8" />
              <path d="M8 21h11a1.8 1.8 0 0 0 1.8-1.8V7" />
            </svg>
            Export QBR
          </a>
          {/* Restored from production (scorecard_qbr_pdf.dart): the QBR as a PDF file, made in the
              browser and saved straight away (a print window was blocked or did nothing). */}
          <a
            href="#"
            data-testid="perf-qbr-pdf"
            aria-disabled={!fac || undefined}
            title={fac ? 'Saves the QBR as a PDF' : 'Nothing to save yet: no building has a benchmark.'}
            onClick={(e) => {
              pre(e);
              if (fac) {
                downloadQbrPdf(fac, peerLabel);
                setPrintBlocked(true);
              }
            }}
            style={sx('font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;' + (fac ? 'color:#2F5C97;' : 'color:#9AA3AD;cursor:default;'))}
          >
            PDF
          </a>
          {printBlocked && (
            <span role="status" style={sx('font-size:14px;color:#1F6B52;')}>
              QBR saved as a PDF.
            </span>
          )}
        </div>

        <div role="tablist" aria-label="Performance" data-testid="perf-tabs" style={sx('flex:0 0 auto;display:flex;gap:18px;border-bottom:1px solid rgba(34,39,46,.13);min-width:0;flex-wrap:nowrap;align-items:flex-end;')}>
          {tabs.filter((t) => PERF_KEEP.includes(t)).map((t) => (
            <a
              key={t}
              href="#"
              role="tab"
              aria-selected={shownTab === t}
              onClick={(e) => {
                pre(e);
                setTab(t);
              }}
              style={sx(tabStyle(shownTab === t))}
            >
              {TAB_LABEL[t]}
            </a>
          ))}
          <MoreMenu
            testId="perf-more"
            items={tabs.filter((t) => !PERF_KEEP.includes(t)).map((t) => ({ id: t, label: TAB_LABEL[t] }))}
            active={shownTab}
            onPick={(t) => setTab(t)}
            tabStyle={tabStyle}
          />
        </div>
        <div style={sx(paneStyle)}>
          {shownTab === 'overview' && <SummaryTab />}
          {shownTab === 'survey' && <SurveyReadyTab />}
          {shownTab === 'creds' && <CredentialsTab />}
          {shownTab === 'admit' && <AdmissionsTab />}
          {shownTab === 'careops' && <EfficiencyTab />}
          {shownTab === 'rebates' && <RebatesTab />}
          {shownTab === 'portfolio' && op && <PortfolioTab />}
          {shownTab === 'distribution' && <DistributionTab />}
          {shownTab === 'trajectory' && <TrajectoryTab />}
        </div>
      </div>
    </PerfContext.Provider>
  );
}
